import { ArrowDropDown, CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { Button, Menu, MenuItem } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import useRights from "@/shared/api/useRights";
import ApiService from "@/api/ApiService";

interface Plant {
  supplierName: string;
  id: number;
  name: string;
  groupingTerm: string;
}

type ReferenceValue = string | number | null | undefined;

type SelectOption =
  | {
      type: "all";
      key: "all";
      label: string;
      ids: [0];
    }
  | {
      type: "plant";
      key: string;
      label: string;
      ids: [number];
      plantId: number;
    }
  | {
      type: "group";
      key: string;
      label: string;
      ids: number[];
      groupingTerm: string;
    };

function normalizeReference(reference: ReferenceValue): number[] {
  const value = reference == null ? "0" : String(reference);

  const parsed = value
    .split(",")
    .map((x) => Number.parseInt(x, 10))
    .filter((x) => Number.isFinite(x));

  if (parsed.length === 0) return [0];

  const unique = Array.from(new Set(parsed));

  if (unique.includes(0)) return [0];

  return unique.sort((a, b) => a - b);
}

function sameIds(a: number[], b: number[]) {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function hasGroupingTerm(value: string | null | undefined) {
  return !!value?.trim();
}

export default function SelectPlantDialog({ reference, onChange }: { reference: ReferenceValue; onChange: (reference: string | null) => void }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = !!anchorEl;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const close = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const rights = useRights();

  const plantId = useMemo(() => {
    if ("PLANT" in rights) {
      const parsed = Number.parseInt(String(rights["PLANT"]), 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }, [rights]);

  const plantQuery = useQuery<Plant[], AxiosError>({
    queryKey: ["getPlantsForList"],
    queryFn: ApiService.getEntitiesWithFilter("plant", { showInList: true }, "calendar"),
  });

  const selectedIds = useMemo(() => normalizeReference(reference), [reference]);

  const options = useMemo<SelectOption[]>(() => {
    const allOption: SelectOption = {
      type: "all",
      key: "all",
      label: "Alle",
      ids: [0],
    };

    if (!plantQuery.data) {
      return [allOption];
    }

    const groupMap = new Map<string, Plant[]>();
    const plantOptions: SelectOption[] = [];

    for (const plant of plantQuery.data) {
      if (hasGroupingTerm(plant.groupingTerm)) {
        const groupingTerm = plant.groupingTerm.trim();
        const groupedPlants = groupMap.get(groupingTerm) ?? [];
        groupedPlants.push(plant);
        groupMap.set(groupingTerm, groupedPlants);
        continue;
      }

      plantOptions.push({
        type: "plant",
        key: `plant-${plant.id}`,
        label: `${plant.name || "Unbekanntes Lieferwerk"} (${plant.supplierName || "Unbekannter Lieferant"})`,
        ids: [plant.id],
        plantId: plant.id,
      });
    }

    const groupOptions: SelectOption[] = Array.from(groupMap.entries()).map(([groupingTerm, plants]) => ({
      type: "group",
      key: `group-${groupingTerm}`,
      label: groupingTerm,
      groupingTerm,
      ids: Array.from(new Set(plants.map((plant) => plant.id))).sort((a, b) => a - b),
    }));

    const sortedOptions = [...plantOptions, ...groupOptions].sort((a, b) => a.label.localeCompare(b.label));

    return [allOption, ...sortedOptions];
  }, [plantQuery.data]);

  const activeGroup = useMemo(() => {
    return options.find((option) => option.type === "group" && sameIds(option.ids, selectedIds));
  }, [options, selectedIds]);

  const isGroupedSelection = !!activeGroup;

  const title = useMemo(() => {
    if (sameIds(selectedIds, [0])) {
      return "Alle";
    }

    if (activeGroup?.type === "group") {
      return activeGroup.label;
    }

    if (selectedIds.length === 1) {
      const selectedId = selectedIds[0];

      const selectedPlantOption = options.find((option) => option.type === "plant" && option.plantId === selectedId);

      return selectedPlantOption?.label ?? "Unbekannt";
    }

    return "Mehrere Werke";
  }, [activeGroup, options, selectedIds]);

  useEffect(() => {
    if (plantId > 0 && "SUPPLIER_VIEW" in rights && String(reference ?? "0") !== String(plantId)) {
      onChange(String(plantId));
    }
  }, [plantId, reference, rights, onChange]);

  const isOptionSelected = useCallback(
    (option: SelectOption) => {
      if (option.type === "all") {
        return sameIds(option.ids, selectedIds);
      }

      if (option.type === "group") {
        return sameIds(option.ids, selectedIds);
      }

      return !isGroupedSelection && selectedIds.includes(option.plantId);
    },
    [selectedIds, isGroupedSelection],
  );

  const handleSelect = useCallback(
    (option: SelectOption) => {
      if (option.type === "all") {
        onChange("0");
        close();
        return;
      }

      if (option.type === "group") {
        onChange(option.ids.join(","));
        return;
      }

      if (isGroupedSelection) {
        onChange(String(option.plantId));
        return;
      }

      let next = selectedIds.filter((id) => id !== 0);

      if (next.includes(option.plantId)) {
        next = next.filter((id) => id !== option.plantId);
      } else {
        next.push(option.plantId);
      }

      next = Array.from(new Set(next)).sort((a, b) => a - b);

      if (next.length === 0) {
        onChange("0");
        return;
      }

      onChange(next.join(","));
    },
    [selectedIds, isGroupedSelection, onChange, close],
  );

  const canOpen = !("SUPPLIER_VIEW" in rights);

  return (
    <div>
      <Button onClick={canOpen ? handleClick : undefined} endIcon={<ArrowDropDown />}>
        {title}
      </Button>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        slotProps={{
          paper: {
            style: {
              maxHeight: "400px",
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.key}
            onClick={() => {
              handleSelect(option);
            }}
          >
            {isOptionSelected(option) ? <CheckBox sx={{ mr: 1 }} /> : <CheckBoxOutlineBlank sx={{ mr: 1 }} />}
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
